import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { mdmsApi } from '@/api/mdmsApi';
import type {
  CreateMdmRequest,
  MdmListRequest,
  UpdateMdmRequest,
} from '@/types/mdm';

const KEYS = {
  all: ['mdms'] as const,
  list: (f: MdmListRequest) => [...KEYS.all, 'list', f] as const,
  accessible: () => [...KEYS.all, 'accessible'] as const,
  detail: (id: number) => [...KEYS.all, 'detail', id] as const,
};

/** Listado paginado de MDMs accesibles (admin). */
export function useMdms(filters: MdmListRequest = {}) {
  return useQuery({
    queryKey: KEYS.list(filters),
    queryFn: () => mdmsApi.list(filters),
  });
}

/** Lista compacta para el selector del header. */
export function useAccessibleMdms() {
  return useQuery({
    queryKey: KEYS.accessible(),
    queryFn: () => mdmsApi.getAccessible(),
    staleTime: 5 * 60 * 1000, // 5 min
  });
}

export function useMdm(id: number | null) {
  return useQuery({
    queryKey: KEYS.detail(id ?? 0),
    queryFn: () => mdmsApi.getById(id!),
    enabled: id != null,
  });
}

export function useCreateMdm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (r: CreateMdmRequest) => mdmsApi.create(r),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useUpdateMdm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, request }: { id: number; request: UpdateMdmRequest }) =>
      mdmsApi.update(id, request),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useDeactivateMdm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => mdmsApi.deactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useReactivateMdm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => mdmsApi.reactivate(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.all }),
  });
}

export function useValidateMdmCredentials() {
  return useMutation({
    mutationFn: (id: number) => mdmsApi.validateCredentials(id),
  });
}

export function useClearAmapiCache() {
  return useMutation({
    mutationFn: () => mdmsApi.clearAmapiCache(),
  });
}
